import { Component, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseComponent } from '../../../../components/base/base.component';
import {DialogService, NotificationService, StudioService, ThemeService} from '@wms/app/services';
import { WmsDialogHeaderComponent } from '../../../../components/wms-dialog-header/wms-dialog-header.component';
import { WmsDialogBodyComponent } from '../../../../components/wms-dialog-body/wms-dialog-body.component';
import { WmsDialogFooterComponent } from '../../../../components/wms-dialog-footer/wms-dialog-footer.component';
import { WmsMessageComponent } from '@wms/app/components/wms-message/wms-message.component'
import { FormsModule } from "@angular/forms";
import { isNewVerGreaterThanCurrentVer} from "@wms/core";
import { AppRef } from "@wms/design-container";
import { PrefabManager } from "@wms/page-workspace";
import { ArtifactsService } from "@wms/settings/artifacts";
import { get, isObject } from "lodash-es";
import { WmsDialogComponent } from '@wms/app/components';

const semver = require('semver');

@Component({
    selector: 'wms-vcs-project-changes-dialog',
    standalone: true,
    templateUrl: './wms-vcs-project-changes-dialog.component.html',
    styleUrls: ['./wms-vcs-project-changes-dialog.component.less'],
    imports: [
        CommonModule,
        WmsDialogComponent,
        WmsDialogHeaderComponent,
        WmsDialogBodyComponent,
        WmsDialogFooterComponent,
        WmsMessageComponent,
        FormsModule
    ]
})
export class WmsVcsProjectChangesDialogComponent extends BaseComponent {
  @Input() prefabConflicts:any;
  @Input() themeConflicts:any;
  private notificationService: NotificationService;
  disableDialogActions: boolean = false
  disabled: boolean = false
  reloadProject:any
  statusMessages:any
  user:any;
  conflictPrefabDetails:any
  prefabConflict:boolean = false
  app: AppRef
  prefabManager : PrefabManager;
  artifactService:ArtifactsService;
  dialogService:DialogService;
  themeService:ThemeService;
  studioService:StudioService;
  isNewVerGreaterThanCurrentVer = isNewVerGreaterThanCurrentVer;

  constructor() {
    super()
    this.notificationService = inject(NotificationService);
    this.prefabManager = inject(PrefabManager);
    this.artifactService = inject(ArtifactsService);
    this.dialogService = inject(DialogService);
    this.themeService = inject(ThemeService);
    this.studioService = inject(StudioService);
    this.app = inject(AppRef);
  }

  ngOnInit(){
    this.prefabConflicts.forEach((prefab:any)=> {
      const upgradableVersions = prefab.upgradableVersions;
      prefab.greaterVersion = '0.0';

      upgradableVersions.forEach((versionObj:any) => {
        if(this.compareVersions(versionObj.version, prefab.greaterVersion) > 0){
          prefab.greaterVersion = versionObj.version;
          prefab.greaterVersionId = versionObj.versionId;
          prefab.greaterReadableVersion = versionObj.readableVersion;
        }
      });
    });
    this.prefabConflicts = this.prefabConflicts.map((obj: { disabled: any; }) => {
      const { disabled, ...rest } = obj;
      return rest;
    });
  }

  closeDialogWithId(){
    if (this.reloadProject) { //reload project is set to true after the prefabs registration is done
      window.location.reload();
    } else {
      this.dialogService.close("projectChangesDialog");
    }
  }
   hideSpinnerMessage() {
    setTimeout(()=> {
      this.statusMessages = null;
    });
  }

   registerPrefab(prefab:any, versionId:any) {
    this.prefabConflict = false
    let wsPrefab:any = this.prefabManager.getWorkspacePrefab(prefab.name),
      versionID = versionId;

      if (!wsPrefab || !wsPrefab.id) {
        return;
      }
      this.disableDialogActions = true;
      this.statusMessages       = {"caption": this.locale.MESSAGE_PROGRESS_PREFAB_UPGRADE, "type": "loading"};
      //set timeout because the synchronous call will freeze the window operations of showing the spinner
      setTimeout(()=> {
        //register the prefab with the latest version in workspace
        let versionId = versionID || wsPrefab.versionId;
        let data = {
          "studioProjectId": this.projectService.getDetails().id,
          "studioArtifactVersion": {
            "artifactId": wsPrefab.id,
            "versionId": versionId
          },
          "forceRegister": prefab.forceRegister ? true : false,
          "upgrade": true
        };
        this.artifactService.updatePrefabVersion( wsPrefab.id, data).then( ()=> {
          prefab.resolved         = true;
          this.disableDialogActions = false;
          this.reloadProject = true;
          this.hideSpinnerMessage();
        },  (response:any)=> {
          let errObj = response.error ? response.error : response;
          this.hideSpinnerMessage();
          this.disableDialogActions = false;
          if (!isObject(errObj)) {
            this.notificationService.show('ERROR','error', errObj)
          } else {
            if (this.prefabManager.isPrefabVersionConflictError(errObj)) {
              this.prefabConflict = true
              this.conflictPrefabDetails = {
                 'error': errObj,
                 'prefabName': wsPrefab.name,
                 'versionId':versionID || wsPrefab.versionId
                };
              this.dialogService.open('prefabConflictDialog', {
                'resolve': {
                  'dialogParams': () => {
                    return {
                      'error': errObj,
                      'prefabName': wsPrefab.name,
                      'versionId':versionID || wsPrefab.versionId,
                      'onOk': () => {
                        prefab.disabled = true;
                      },
                      'forceRegisterSuccess':  () => {
                        prefab.resolved = true;
                        this.reloadProject = true;
                      }
                    };
                  }
                }
              });
            } else if (this.prefabManager.isCircularDependencyError(errObj)) {

              this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, get(errObj, 'errors.error[0].message'))

            } else {
              this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, this.locale.MESSAGE_ERROR_PREFAB_REGISTER_FAILED_DESC)

            }
          }
        });
      }, 500);
  }

   registerTheme(theme:any) {
    let wsTheme = this.themeService.getStudioTheme(theme.name);
    if (!wsTheme || !wsTheme.id) {
      return;
    }
    this.disableDialogActions = true;
    this.statusMessages       = {"caption": this.locale.MESSAGE_PROGRESS_THEME_UPGRADE, "type": "loading"};
    //register the theme with the lastest version in workspace
    this.themeService.register({ artifactId : wsTheme.id, versionId : wsTheme.versionId})
      .then( ()=> {
        theme.resolved         = true;
        this.disableDialogActions = false;
        this.reloadProject = true;
        this.hideSpinnerMessage();
      },  (err:any)=> {
        this.hideSpinnerMessage();
        this.disableDialogActions = false;
        this.notificationService.show('ERROR', 'error',err)
      });
  }

  compareVersions(version: string, greaterVersion: string): number {

    const currentVerParts = version.split('.').map(part => parseInt(part, 10));
    const latestVerParts = greaterVersion.split('.').map(part => parseInt(part, 10));

    for (let i = 0; i < Math.max(currentVerParts.length, latestVerParts.length); i++) {
      const part1 = currentVerParts[i] || 0;
      const part2 = latestVerParts[i] || 0;

      if (part1 !== part2) {
        return part1 - part2;
      }
    }

    return 0;
  }

}
